import { useCallback, useRef, useState, type FormEvent } from 'react'
import { useLeads, type LeadKind } from '../contexts/LeadsContext'
import { formDataToRecord, submitLead, type SheetKey } from '../utils/sheets'
import { trackPixel } from '../utils/pixel'

type Status = 'idle' | 'sending' | 'error' | 'success'

export function useLeadSubmit(sheetKey: SheetKey, leadKind: LeadKind) {
  const { addLead } = useLeads()
  const [status, setStatus] = useState<Status>('idle')
  const [reference, setReference] = useState<string | null>(null)
  const lastData = useRef<Record<string, string> | null>(null)

  const send = useCallback(
    async (data: Record<string, string>) => {
      setStatus('sending')
      lastData.current = data
      const result = await submitLead(sheetKey, data)
      if (!result.ok) {
        setStatus('error')
        return
      }
      addLead(leadKind, data, result.id)
      trackPixel('Lead', { content_category: sheetKey })
      // Solo se muestra el ID cuando el script pudo devolverlo (con no-cors es opaco).
      setReference(result.id ?? null)
      setStatus('success')
    },
    [addLead, leadKind, sheetKey],
  )

  const submitForm = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      void send(formDataToRecord(event.currentTarget))
    },
    [send],
  )

  const retry = useCallback(() => {
    if (lastData.current) void send(lastData.current)
  }, [send])

  return {
    status,
    reference,
    sending: status === 'sending',
    submitted: status === 'success',
    submitForm,
    retry,
  }
}
